import AlertBox from '@/components/AlertBox';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import { FormFieldRadioGroup } from '@/components/FormFieldRadioGroup';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { useClientBillingPreferenceGet, useClientBillingPreferenceUpdate } from '@/services/clients';
import { useUserStore } from '@/stores/user';
import { InvoiceType } from '@/types/accountsReceivable.types';
import { FbsFileType, RecapFileType, RecapFrequency, StatementFileType } from '@/types/client.types';
import { ExtendedUserResponseSchema } from '@/types/user.types';
import {
  BILLING_TYPE,
  FBS_FILE_TYPE,
  FINANCIAL_CHARGES_OPTIONS,
  RECAP_FILE_TYPE,
  RECAP_FREQUENCY,
  STATEMENT_FILE_TYPE,
} from '@/utils/constant';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

const ALLOWED_TEAM_KEYS = ['BILLING', 'ACCOUNTING', 'ADMIN'];

export const hasBillingPreferenceEditAccess = (user: ExtendedUserResponseSchema | null) => {
  if (!user) return false;

  const isAdmin = user.roleId === 'ADMIN';
  if (isAdmin) return true;

  const isInAllowedTeam = user.teams?.some((mapping) => ALLOWED_TEAM_KEYS.includes(mapping.key));
  if (isInAllowedTeam) return true;

  const isSupervisor = user.teams?.some((mapping) => mapping.supervisorId === user.userId);
  if (isSupervisor) return true;

  return false;
};

const parseFinancialCharges = (val: unknown): boolean | undefined => {
  if (val === 'true' || val === true) return true;
  if (val === 'false' || val === false) return false;
  return undefined;
};

export const BillingPreferenceSchema = z
  .object({
    billingType: z.nativeEnum(InvoiceType, { required_error: 'Billing type is required' }),
    isArRequired: z.boolean().optional(),
    isInvoiceRequired: z.boolean().optional(),
    isRecapRequired: z.boolean().optional(),
    recapFileType: z.nativeEnum(RecapFileType).nullish(),
    recapFrequency: z.nativeEnum(RecapFrequency).nullish(),
    isFbsRequired: z.boolean().optional(),
    fbsFileType: z.nativeEnum(FbsFileType).nullish(),
    statementFileType: z.nativeEnum(StatementFileType).default(StatementFileType.PDF),
    applyFinancialCharges: z.preprocess(parseFinancialCharges, z.boolean().optional()),
  })
  .superRefine((data, ctx) => {
    if (data.isRecapRequired) {
      if (!data.recapFileType)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Recap file type is required when recap is required',
          path: ['recapFileType'],
        });

      if (!data.recapFrequency)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Recap frequency is required when recap is required',
          path: ['recapFrequency'],
        });
    }

    if (data.isFbsRequired && !data.fbsFileType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FBS file type is required when FBS is required',
        path: ['fbsFileType'],
      });
    }
  });

const BillingPreferencesUpdate = () => {
  const user = useUserStore((state) => state.user);
  const { clientId } = useParams();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const navigate = useNavigate();
  const {
    data: billingPreferenceData,
    isFetching: billingPreferenceFetching,
    isError,
  } = useClientBillingPreferenceGet(clientId);

  const { mutate: updateBillingPreference, status: updatingBillingPreference } = useClientBillingPreferenceUpdate();

  const form = useForm({
    resolver: zodResolver(BillingPreferenceSchema),
    mode: 'onChange',
  });

  const {
    control,
    reset,
    formState: { isValid, isDirty },
    getValues,
    watch,
  } = form;

  useEffect(() => {
    if (!hasBillingPreferenceEditAccess(user)) {
      navigate('/clients');
      toast({
        description: 'You do not have access to edit billing preferences.',
        variant: 'destructive',
      });
    }
  }, [user]);

  useEffect(() => {
    if (billingPreferenceData?.data) {
      const billingPreference = billingPreferenceData?.data;
      reset({
        billingType: billingPreference?.billingType,
        isArRequired: billingPreference?.isArRequired,
        isInvoiceRequired: billingPreference?.isInvoiceRequired,
        isRecapRequired: billingPreference?.isRecapRequired,
        recapFileType: billingPreference?.recapFileType,
        recapFrequency: billingPreference?.recapFrequency,
        isFbsRequired: billingPreference?.isFbsRequired,
        fbsFileType: billingPreference?.fbsFileType,
        statementFileType: billingPreference?.statementFileType ?? StatementFileType.PDF,
        applyFinancialCharges:
          billingPreference?.applyFinancialCharges !== undefined
            ? String(billingPreference.applyFinancialCharges)
            : undefined,
      });
    }
  }, [billingPreferenceData]);

  const saveHandler = () => {
    const values = getValues() as z.infer<typeof BillingPreferenceSchema>;
    const applyFinancialCharges = parseFinancialCharges(getValues('applyFinancialCharges'));

    if (clientId)
      updateBillingPreference(
        {
          clientId: clientId,
          data: {
            billingType: values.billingType,
            isArRequired: values.isArRequired,
            isInvoiceRequired: values.isInvoiceRequired,
            isRecapRequired: values.isRecapRequired,
            recapFileType: values.recapFileType,
            recapFrequency: values.recapFrequency,
            isFbsRequired: values.isFbsRequired,
            fbsFileType: values.fbsFileType,
            statementFileType: values.statementFileType,
            applyFinancialCharges,
          },
        },
        {
          onSuccess: () => {
            setIsAlertOpen(false);
            navigate('/clients');
            toast({
              description: `Billing preferences for ${billingPreferenceData?.data.client?.clientName} has been updated.`,
              variant: 'default',
            });
          },
          onError: (e) => {
            setIsAlertOpen(false);
            displayError(e.response?.data, 5000);
          },
        }
      );
  };

  const isRecapRequired = watch('isRecapRequired') as boolean;
  const isFbsRequired = watch('isFbsRequired') as boolean;

  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="client billing preference">
      <Loader isLoading={billingPreferenceFetching}>
        <div className="w-full scrollbar-hide gap-24 h-screen flex flex-col justify-between">
          <div>
            <ToolBar title={billingPreferenceData?.data.client?.clientName} />
            <FormProvider {...form}>
              <div className="flex flex-col gap-8 w-[50%]">
                <span>Billing Preferences </span>
                <FormFieldSelect
                  label="Billing Type"
                  name="billingType"
                  options={BILLING_TYPE}
                  required
                  placeholder="Select Billing Type"
                />
                <div className="flex flex-col gap-6">
                  <FormFieldCheckbox label="A/R Statements" name="isArRequired" control={control} />
                  <FormFieldCheckbox label="Invoice Required" name="isInvoiceRequired" control={control} />
                  <FormFieldCheckbox label="Recap Required" name="isRecapRequired" control={control} />
                  {isRecapRequired && (
                    <React.Fragment>
                      <FormFieldRadioGroup
                        label="Choose the file type for Recap:"
                        name="recapFileType"
                        options={RECAP_FILE_TYPE}
                        direction="horizontal"
                      />
                      <FormFieldRadioGroup
                        label="Choose Recap Frequency:"
                        name="recapFrequency"
                        options={RECAP_FREQUENCY}
                        direction="horizontal"
                      />
                    </React.Fragment>
                  )}
                  <FormFieldCheckbox label="Share Final Billing Summary" name="isFbsRequired" control={control} />
                  {isFbsRequired && (
                    <FormFieldRadioGroup
                      label="Choose the file type for Final Billing Summary:"
                      name="fbsFileType"
                      options={FBS_FILE_TYPE}
                      direction="horizontal"
                    />
                  )}
                </div>
                <div className="flex flex-col gap-6">
                  <span className="font-semibold">Statement Preference</span>
                  <FormFieldRadioGroup
                    label="Choose the file type for Statement:"
                    name="statementFileType"
                    options={STATEMENT_FILE_TYPE}
                    direction="horizontal"
                    required
                  />
                  <FormFieldRadioGroup
                    label="Financial Charges:"
                    name="applyFinancialCharges"
                    options={FINANCIAL_CHARGES_OPTIONS}
                    direction="horizontal"
                  />
                </div>
              </div>
            </FormProvider>
          </div>
          <div className={`flex justify-end mt-4 mb-8 w-full gap-2`}>
            <AlertBox
              open={isAlertOpen}
              triggerButton={
                <Button
                  variant="outline"
                  className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white "
                  onClick={() => {
                    setIsAlertOpen(true);
                  }}
                  disabled={!isValid || !isDirty}
                >
                  Save
                </Button>
              }
              title="Do you want to update the record?"
              description="This action will overwrite the previous data with the new data entered."
              cancelBtnLabel="Cancel"
              confirmBtnLabel="Save"
              cancelBtnClick={() => {
                setIsAlertOpen(false);
              }}
              confirmBtnClick={saveHandler}
              isLoading={updatingBillingPreference === 'pending'}
            />
          </div>
        </div>
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};

export default BillingPreferencesUpdate;
